import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';
import { HelmetProvider } from "react-helmet-async";

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

// Industrial bolt-pattern SVG background
const BoltPattern = () => (
  <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="bolts" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="1.5" />
        <circle cx="30" cy="30" r="4"  fill="white" />
        <line x1="30" y1="20" x2="30" y2="14" stroke="white" strokeWidth="1.5" />
        <line x1="30" y1="40" x2="30" y2="46" stroke="white" strokeWidth="1.5" />
        <line x1="20" y1="30" x2="14" y2="30" stroke="white" strokeWidth="1.5" />
        <line x1="40" y1="30" x2="46" y2="30" stroke="white" strokeWidth="1.5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#bolts)" />
  </svg>
);

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featured,   setFeatured]   = useState([]);
  const [dealers,    setDealers]    = useState([]);
  const [showColdStartMsg, setShowColdStartMsg] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setShowColdStartMsg(true), 3000);

    Promise.all([
      api.get('/categories/all').then(r => setCategories(r.data.data || [])),
      api.get('/products?isFeatured=true&limit=8').then(r => setFeatured(r.data.data || [])),
      api.get('/dealers/all').then(r => setDealers(r.data.data || [])),
    ]).finally(() => {
      clearTimeout(timer);
      setShowColdStartMsg(false);
    });

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />
      <main className="flex-1 pt-20">
        {showColdStartMsg && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-on-surface text-white text-sm px-4 py-2 rounded-full shadow-lg">
            Just a moment, waking up the server...
          </div>
        )}

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="relative min-h-[00px] flex items-center bg-on-surface overflow-hidden">
  {/* Blurred background image layer */}
  <div
    className="absolute inset-0 bg-cover bg-center blur-[6px] scale-100"
    style={{ backgroundImage: "url('/images/hero-bg.png')" }}
  />

  {/* Dark overlay so white text stays readable */}
  <div className="absolute inset-0 bg-on-surface/70" />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-10 py-24">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-0.5 bg-primary-container" />
                <span className="font-label text-xs uppercase tracking-[0.3em] text-gray-400">Est. Industrial Precision</span>
              </div>
              <h1 className="font-sans font-black text-6xl md:text-8xl uppercase leading-none tracking-tighter text-white mb-6">
                Central<br />
                <span className="">Fasteners</span>
              </h1>
              <p className="font-body text-lg text-gray-400 max-w-xl leading-relaxed mb-10">
                Authorized Distributor to sell the entire range of Unbrako & DFL. High-Tenesile Alloy & Stainless Steel Fasteners.
              </p>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => navigate('/products')} className="btn-primary text-base px-8 py-4">
                  <span className="material-symbols-outlined">inventory_2</span>
                  Browse Products
                </button>
                <button onClick={() => navigate('/price-list')} className="btn-secondary text-base px-8 py-4 border-gray-600 text-gray-300 hover:bg-white hover:text-on-surface">
                  <span className="material-symbols-outlined">picture_as_pdf</span>
                  Price List
                </button>
              </div>
            </div>
          </div>

          {/* Decorative hex grid */}
          <div className="absolute right-0 top-0 h-full w-1/3 opacity-10 hidden xl:block">
            <div className="grid grid-cols-5 gap-2 h-full content-center p-8">
              {[...Array(50)].map((_, i) => (
                <div key={i} className="w-10 h-10 border border-white/40 rotate-45 flex-shrink-0" />
              ))}
            </div>
          </div>
        </section>

        {/* ── Stats bar ─────────────────────────────────────────────────── */}
        <section className="bg-primary-container text-white">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/20">
              {[
                { icon: 'bolt',       label: 'Product Range',  value: '5000+'  },
                { icon: 'factory',    label: 'Categories',     value: '20+'   },
                { icon: 'handshake',  label: 'Happy Clients',  value: '1000+' },
                { icon: 'verified',   label: 'Years Experience',   value: '25+'   },
              ].map(({ icon, label, value }) => (
                <div key={label} className="py-6 px-6 text-center">
                  <span className="material-symbols-outlined text-[28px] opacity-80 mb-1">{icon}</span>
                  <div className="font-headline font-black text-3xl">{value}</div>
                  <div className="font-label text-xs uppercase tracking-widest opacity-80 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* ── Categories ────────────────────────────────────────────────── */}
        <section className="py-20 px-6 md:px-10 max-w-screen-2xl mx-auto">
          <div className="mb-12">
            <div className="accent-bar" />
            <p className="font-Inconsolata font-black text-3xl md:text-4xl  uppercase">Product Categories</p>
            <p className="font-body text-secondary mt-3">Browse our complete range of industrial fasteners.</p>
          </div>

          {categories.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-48 skeleton" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map(cat => (
                <Link key={cat._id} to={`/products?category=${cat._id}`}
                  className="group relative overflow-hidden bg-on-surface card-industrial aspect-square flex flex-col items-center justify-center gap-3 p-6 text-center">
                  {cat.image
                    ? <img src={cat.image} alt={cat.name}
                        className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-300" />
                    : <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900" />
                  }
                  <div className="relative z-10">
                    <span className="material-symbols-outlined text-[48px] text-primary-container mb-2">construction</span>
                    <h3 className="font-headline font-black text-xl uppercase tracking-tight text-white">{cat.name}</h3>
                    {cat.description && (
                      <p className="font-body text-xs text-gray-400 mt-1 line-clamp-2">{cat.description}</p>
                    )}
                  </div>
                  <div className="absolute bottom-0 left-0 w-0 h-1 bg-primary-container group-hover:w-full transition-all duration-300" />
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/products" className="btn-secondary">
              View All Products
              <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
            </Link>
          </div>
        </section>

        {/* ── Featured Products ──────────────────────────────────────────── */}
        {featured.length > 0 && (
          <section className="py-20 px-6 md:px-10 bg-surface-container-low">
            <div className="max-w-screen-2xl mx-auto">
              <div className="mb-12">
                <div className="accent-bar" />
                <h2 className="font-Inconsolata font-black text-3xl md:text-4xl uppercase">Featured Products</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {featured.map(p => (
                  <Link key={p._id} to={`/products/${p._id}`}
                    className="group bg-white card-industrial flex flex-col overflow-hidden">
                    <div className="h-48 bg-surface-container overflow-hidden">
                      {p.images?.[0]
                        ? <img src={p.images[0]} alt={p.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full flex items-center justify-center">
                            <span className="material-symbols-outlined text-[64px] text-outline-variant">bolt</span>
                          </div>
                      }
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <p className="font-label text-xs text-secondary uppercase tracking-widest">{p.category?.name}</p>
                      <h3 className="font-headline font-bold text-lg uppercase tracking-tight mt-1 text-on-surface">{p.name}</h3>
                      {p.material && <p className="font-body text-xs text-secondary mt-1">{p.material}</p>}
                      <div className="mt-auto pt-3 flex items-center justify-between">
                        <span className="font-label text-xs text-secondary">{p.sizes?.length || 0} sizes</span>
                        <span className="font-label text-xs text-primary-container font-bold uppercase tracking-wider group-hover:underline">
                          View →
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Our Dealerships ──────────────────────────────────────────── */}
{dealers.length > 0 && (
  <section className="py-20 px-6 md:px-10 bg-surface-container-low">
    <div className="max-w-screen-2xl mx-auto">
      <div className="mb-12 text-center">
        <div className="accent-bar mx-auto" />
        <p className="font-Inconsolata font-black text-3xl md:text-4xl uppercase">Dealerships</p>
        <p className="font-body text-secondary mt-3">Authorized dealers for leading industrial brands.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
        {dealers.map(d => {
          const LogoTag = d.website ? 'a' : 'div';
          const linkProps = d.website ? { href: d.website, target: '_blank', rel: 'noreferrer' } : {};
          return (
            <LogoTag key={d._id} {...linkProps}
              className="group bg-white border border-outline-variant/20 aspect-[3/2] flex items-center justify-center p-6 hover:border-primary-container transition-colors">
              <img src={d.logo} alt={d.name} title={d.name}
                className="max-w-full max-h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" />
            </LogoTag>
          );
        })}
      </div>
    </div>
  </section>
)}

        {/* ── CTA strip ─────────────────────────────────────────────────── */}
        <section className="bg-on-surface py-16 px-6 md:px-10">
          <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-Inconsolata font-black text-3xl md:text-4xl text-white uppercase ">
                Need a specific fastener?
              </h2>
              <p className="font-body text-gray-400 mt-2">Add products to your inquiry list and we'll get back to you.</p>
            </div>
            <div className="flex gap-4">
              <Link to="/products"  className="btn-primary whitespace-nowrap">Browse Products</Link>
              <Link to="/feedback"  className="btn-secondary border-gray-600 text-gray-300 hover:bg-white hover:text-on-surface whitespace-nowrap">Contact Us</Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
